import { createAsyncThunk } from '@reduxjs/toolkit'

import { Action, ActionResponse, ActionType } from '../../types/actionTypes'
import {
  getAllActions,
  addActionApi,
  deleteActionApi,
  getActionbyIdApi,
  updateActionApi,
  getActionbyAuditApi,
  getActionbyIncidentApi,
  getActionbyRisqueApi,
  getActionbyProjectApi,
  getActionbySecurityCheckFamilyApi,
  addActionToProjetApi,
  removeActionFromProjetApi,
  getActionsByMissionIdApi,
  getExistingControlbySecurityControlyApi,
  getSubDomainbySecurityControlyApi,
  getActionsBySubDomainIdApi,
} from '../../api/actionApi'
import { ExistingControl } from '../../types/existingControlTypes'
import { SubDomain } from '../../types/subDomainTypes'
import { getActionsFromRelatedControls } from '../../api/securityControlsApi'
import { getActionsWithoutProjectByMissionId } from '../../api/missionsApi'
import { parseActionFloatFields } from '../../utils/parseFloatFields'
import { handleError } from '../../utils/errorHandler'

export const getActions = createAsyncThunk<
  {
    actions: Action[]
    total: number
    totalPages: number
    status?: number
    erreur?: boolean
    message?: string
  },
  { search?: string; page?: number; pageSize?: number },
  { rejectValue: string }
>('action/getAllActions', async ({ search, page, pageSize }, thunkAPI) => {
  try {
    const response = await getAllActions({ search, page, pageSize })
    return {
      actions: response.actions,
      total: response.total,
      totalPages: response.totalPages,
      status: response.status,
      message: response.message,
      erreur: response.erreur,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchActionsByRelatedControlId = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>('actions/fetchByRelatedControlId', async (id, thunkAPI) => {
  try {
    const actions = await getActionsFromRelatedControls(id)
    return actions
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const fetchActionsWithoutProject = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>('actions/fetchWithoutProject', async (missionId, thunkAPI) => {
  try {
    const response = await getActionsWithoutProjectByMissionId(missionId)
    return response.actions
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const deleteAction = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('action/deleteAction', async (ActionId, thunkAPI) => {
  try {
    await deleteActionApi(ActionId)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const addAction = createAsyncThunk<
  Action,
  Action,
  { rejectValue: string }
>('action/addAction', async (newAction, thunkAPI) => {
  try {
    // Parse float fields before sending to API
    const parsedAction = parseActionFloatFields(newAction)
    const response = await addActionApi(parsedAction)
    return response
  } catch (error: any) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionbyId = createAsyncThunk<
  Action,
  string,
  { rejectValue: string }
>('action/getActionbyId', async (ActionId, thunkAPI) => {
  try {
    const response = await getActionbyIdApi(ActionId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const updateAction = createAsyncThunk<
  Action,
  { updatedAction: Action; _id: string },
  { rejectValue: string }
>('action/updateAction', async ({ updatedAction, _id }, thunkAPI) => {
  try {
    // Parse float fields before sending to API
    const parsedAction = parseActionFloatFields(updatedAction)
    const response = await updateActionApi(parsedAction, _id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionbyAudit = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>('action/getActionbyAudit', async (AuditId, thunkAPI) => {
  try {
    const response = await getActionbyAuditApi(AuditId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionbyIncident = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>('action/getActionbyIncident', async (IncidentId, thunkAPI) => {
  try {
    const response = await getActionbyIncidentApi(IncidentId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionbyRisque = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>('action/getActionbyRisque', async (RisqueId, thunkAPI) => {
  try {
    const response = await getActionbyRisqueApi(RisqueId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionbyProject = createAsyncThunk<
  ActionResponse,
  string,
  { rejectValue: string }
>('action/getActionbyProject', async (ProjectId, thunkAPI) => {
  try {
    const response = await getActionbyProjectApi(ProjectId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionbySecurityCheckFamily = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>(
  'action/getActionbySecurityCheckFamily',
  async (securityCheckFamilyId, thunkAPI) => {
    try {
      const response = await getActionbySecurityCheckFamilyApi(
        securityCheckFamilyId
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const addActionsToProject = createAsyncThunk<
  ActionResponse,
  { ActionId: string[]; _id: string },
  { rejectValue: string }
>('action/addActionsToProject', async ({ ActionId, _id }, thunkAPI) => {
  try {
    const response = await addActionToProjetApi(ActionId, _id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const removeActionFromProject = createAsyncThunk<
  ActionResponse,
  { actions: ActionType; _id: string },
  { rejectValue: string }
>('action/RemoveActionFromProject', async ({ actions, _id }, thunkAPI) => {
  try {
    const response = await removeActionFromProjetApi(actions, _id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionsByMissionId = createAsyncThunk<
  { actions: Action[]; total: number; totalPages: number },
  { page?: number; pageSize?: number; MissionId: string },
  { rejectValue: string }
>(
  'action/getActionsByMissionId',
  async ({ page, pageSize, MissionId }, thunkAPI) => {
    try {
      const response = await getActionsByMissionIdApi(MissionId, page, pageSize)
      return {
        actions: response.actions,
        total: response.total,
        totalPages: response.totalPages,
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getAExistingControlbySecurityControl = createAsyncThunk<
  ExistingControl[],
  string,
  { rejectValue: string }
>(
  'existingControls/getExistingControlbySecurityControl',
  async (securityControlsId, thunkAPI) => {
    try {
      const response =
        await getExistingControlbySecurityControlyApi(securityControlsId)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getSubDomainbySecurityControl = createAsyncThunk<
  SubDomain[],
  string,
  { rejectValue: string }
>(
  'SubDomainbySecurity/getSubDomainbySecurityControl',
  async (securityControlsId, thunkAPI) => {
    try {
      const response =
        await getSubDomainbySecurityControlyApi(securityControlsId)
      console.log(response)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getActionsBySubDomainId = createAsyncThunk<
  Action[],
  string,
  { rejectValue: string }
>('action/getActionsBySubDomains', async (SubDomainId, thunkAPI) => {
  try {
    const response = await getActionsBySubDomainIdApi(SubDomainId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
